import clsx from 'clsx';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { DeviationDto } from 'src/generated/services/TFinancialApi';
import { ButtonTab } from 'src/presentations/components/organisms/actionDetails/courier/deviationDetails/ButtonTab';
import IconArrowLeft from 'src/assets/images/icons/arrow-left_ico.svg';
import IconArrowRight from 'src/assets/images/icons/arrow-right_ico.svg';
import { useTourContext } from 'src/presentations/guided-tours';

import { IDeviationsTabsProps } from './types';

export const DeviationsTabs: FC<IDeviationsTabsProps> = (props) => {
  const { deviations, onChange, selectedDeviation } = props;
  const tour = useTourContext().appTour.registerTour('actionDetails');

  const [itemsPerView, setItemsPerView] = useState<number>(0);
  const [rootWidth, setRootWidth] = useState<number | null>(null);
  const [tabsWidth, setTabsWidth] = useState<number | null>(null);
  const [isShowArrows, setIsShowArrows] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const tabsRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tabsRef.current && rootRef.current) {
      setTimeout(() => {
        if (!tabsRef.current || !rootRef.current) return;
        let currentWidth = 0;
        let totalWidth = 0;
        let visibleItems = 0;
        const rootOffsetWidth = rootRef.current.offsetWidth;
        const maxWidth = rootOffsetWidth - 110;
        const buttons = tabsRef.current.querySelectorAll(':scope > div');
        buttons.forEach((button) => {
          const newCurrentWidth = currentWidth + button.clientWidth + 16;
          if (newCurrentWidth <= maxWidth) {
            currentWidth = newCurrentWidth;
            visibleItems += 1;
          }
          totalWidth += button.clientWidth + 16;
        });
        setRootWidth(rootOffsetWidth);

        if (totalWidth > rootOffsetWidth) {
          setTabsWidth(currentWidth - 14);
          setIsShowArrows(true);
          setItemsPerView(Math.ceil(buttons?.length / (visibleItems || 1)));
        }
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsRef.current, rootRef.current]);

  const onClickTab = useCallback((item: DeviationDto) => {
    onChange(item);

    if (isShowArrows) {
      setTimeout(() => {
        if (!tabsRef.current || !rootRef.current) return;
        let currentWidth = 0;
        let totalWidth = 0;
        const maxWidth = rootWidth - 110;
        const buttons = tabsRef.current.querySelectorAll(':scope > div');
        buttons.forEach((button) => {
          const newCurrentWidth = currentWidth + button.clientWidth + 16;
          if (newCurrentWidth <= maxWidth) {
            currentWidth = newCurrentWidth;
          }
          totalWidth += button.clientWidth + 16;
        });

        if (totalWidth > rootWidth) {
          setTabsWidth(currentWidth - 14);
        }
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNextTab = useCallback(() => {
    const newPage = page + 1;
    if (newPage <= itemsPerView) {
      setPage(newPage);
    }
  }, [page, itemsPerView]);

  const onPrevTab = useCallback(() => {
    const newPage = page - 1;
    if (newPage > 0) {
      setPage(newPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerView]);

  return (
    <div className='border-b border-neutral-20 mb-24 px-24 py-16'>
      <div ref={rootRef} className='flex items-center gap-18'>
        {isShowArrows && (
          <button
            type='button'
            disabled={page <= 1}
            onClick={onPrevTab}
            className={clsx(
              'w-42 h-42 rounded-full bg-neutral-10 border border-neutral-20 transition-all duration-300 hover:border-neutral-40 flex items-center justify-center',
              page <= 1 && 'opacity-40 cursor-not-allowed'
            )}
          >
            <img src={IconArrowLeft} alt='Prev' />
          </button>
        )}
        <div className='overflow-hidden flex-1'>
          <div id={tour.registerStep('deviationPills')} className='overflow-auto scrollbar-hide w-full'>
            <div
              ref={tabsRef}
              className='flex gap-16 items-center transition-transform duration-300'
              style={{ transform: `translateX(-${(page - 1) * 100}%)` }}
            >
              {deviations?.map((item: DeviationDto, index: number) => (
                <div key={`deviation-${item.id}`}>
                  <ButtonTab data={item} onChange={() => onClickTab(item)} index={index} isActive={item.id === selectedDeviation?.id} />
                </div>
              ))}
            </div>
          </div>
        </div>
        {isShowArrows && (
          <button
            type='button'
            onClick={onNextTab}
            disabled={page >= itemsPerView}
            className={clsx(
              'w-42 h-42 rounded-full bg-neutral-10 border border-neutral-20 transition-all duration-300 hover:border-neutral-40 flex items-center justify-center',
              page >= itemsPerView && 'opacity-40 cursor-not-allowed'
            )}
          >
            <img src={IconArrowRight} alt='Prev' />
          </button>
        )}
      </div>
    </div>
  );
};
